import React, { useState, useRef } from "react";
import { useFormik } from "formik";

import * as Yup from "yup";
import ImageEditor from './Editor/ImageEditor';
import BlogSeoFields from './BlogSeoFields';
import BlogFaqFields from './BlogFaqFields';
import ReactTags from 'react-tag-autocomplete'

import 'react-tagsinput/react-tagsinput.css'

import GalleryModal from "./Gallery/GalleryModal";

import { normalizeRichTextHtml, stripHtml } from "./Forms/sharedUtils";


const blogScheme = Yup.object().shape({
  title: Yup.string().required("Required"),
  image_url: Yup.string().required("Required"),
  content: Yup.string().required("Required"),
  status: Yup.string().oneOf(["draft", "published", "archived"]).required("Required"),
  published_at: Yup.string().when("status", {
    is: "published",
    then: (schema) => schema.required("Published at is required"),
  }),
  seo_title: Yup.string().max(60, "Keep the SEO title at 60 characters or fewer"),
  seo_description: Yup.string().max(160, "Keep the SEO description at 160 characters or fewer"),
  // author_name: Yup.string(),
});

const sanitizePreviewHtml = (html) => {
  if (!html || typeof DOMParser === "undefined") return html || "";

  const allowedTags = new Set([
    "A", "BLOCKQUOTE", "BR", "CAPTION", "COL", "COLGROUP", "DIV", "EM", "H1", "H2", "H3",
    "H4", "H5", "H6", "IFRAME", "IMG", "LI", "OL", "P", "S", "SPAN", "STRONG", "TABLE", "TBODY", "TD",
    "TFOOT", "TH", "THEAD", "TR", "U", "UL", "VIDEO"
  ]);
  const allowedAttributes = new Set([
    "align", "allowfullscreen", "class", "colspan", "frameborder", "headers", "height", "href",
    "rel", "rowspan", "scope", "src", "target", "title", "width"
  ]);
  const documentFragment = new DOMParser().parseFromString(`<div>${html}</div>`, "text/html");

  [...documentFragment.body.querySelectorAll("*")].forEach((element) => {
    if (!allowedTags.has(element.tagName)) {
      element.replaceWith(...element.childNodes);
      return;
    }

    [...element.attributes].forEach((attribute) => {
      if (!allowedAttributes.has(attribute.name.toLowerCase())) element.removeAttribute(attribute.name);
    });

    ["href", "src"].forEach((attributeName) => {
      const value = element.getAttribute(attributeName);
      if (value && !/^(https?:|mailto:|tel:|\/)/i.test(value)) element.removeAttribute(attributeName);
    });
  });

  documentFragment.body.querySelectorAll("a[target='_blank']").forEach((link) => {
    link.setAttribute("rel", "noopener noreferrer");
  });
  return documentFragment.body.firstElementChild?.innerHTML || "";
};


const BlogEdit = (props) => {
  const { blog } = props;
  const [isLoading, setIsLoading] = useState(false);
  const [openingModal, setOpeningModal] = useState(false);
  const [composerMode, setComposerMode] = useState("split");
  const formRef = useRef();

  const handleSave = (data) => {
    const tag_list = (data.tag_list || []).map(({ name }) => (name)).join(',');
    setIsLoading(true);
    fetch(`/admin/blogs/${blog.slug}.json`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')
          ?.content,
      },
      body: JSON.stringify({
        blog: {
          ...data,
          content: normalizeRichTextHtml(data.content),
          bottom_content: normalizeRichTextHtml(data.bottom_content),
          tag_list
        }
      }),
    })
      .then((resp) => {
        return resp.json();
      })
      .then((resp) => {
        console.log("resp", resp);
        setIsLoading(false);
        window.location.href = `/admin/blogs/${resp.resource.slug}/edit`;
      })
      .catch((error) => {
        console.log("error", error);
        setIsLoading(false);
      });
  };
  const tag_list = (blog?.tag_list || []).map((x) => ({ id: x, name: x }));
  const published_at = blog?.published_at ? blog.published_at.slice(0, 16) : "";
  const formik = useFormik({
    initialValues: {
      ...(blog || {}),
      content: normalizeRichTextHtml(blog?.content),
      bottom_content: normalizeRichTextHtml(blog?.bottom_content),
      published_at,
      tag_list,
      faqs: blog?.faqs || []
    },
    validationSchema: blogScheme, // replace with your actual validation schema
    onSubmit: handleSave, // replace with your form submission function
  });

  const {
    values,
    touched,
    errors,
    handleChange,
    handleBlur,
    handleSubmit,
    setFieldValue,
  } = formik;
  const handleSelect = (images) => {
    setOpeningModal(false);
    setFieldValue("image_url", images ? images[0].image_url : null);
  };
  const onDeleteTag = (idx) => {
    const tags = (values.tag_list || []).slice(0);
    tags.splice(idx, 1)
    setFieldValue('tag_list', tags);
  }
  const onAddTag = (tag) => {
    const tags = [...(values.tag_list || []), { name: tag.name, id: tag.name }]
    setFieldValue('tag_list', tags);
  }
  const suggestions = [
    { id: "Bananas", name: "Bananas" },
    { id: "Mangos", name: "Mangos" },
    { id: "Lemons", name: "Lemons" },
    { id: "Apricots", name: "Apricots" }
  ]
  const previewDate = values.published_at
    ? new Date(values.published_at).toLocaleString("en-GB", { dateStyle: "long", timeStyle: "short" })
    : "Not scheduled";

  return (
    <React.Fragment>
      <form onSubmit={handleSubmit} ref={formRef}>
        <div className="row with-forms">

          <div className="col-md-6">
            <h5>
              Listing Title
              <sup className="required">*</sup>
            </h5>
            <input
              type="text"
              name="title"
              id="title"
              placeholder="Blog Title"
              autoComplete="off"
              value={`${values.title || ""}`}
              onChange={handleChange}
              onBlur={handleBlur}
              required={true}
              className={
                errors.title && touched.title
                  ? "form-input error"
                  : "form-input"
              }
            />
          </div>
          <div className="col-md-6">
            <h5>Slug</h5>
            <input
              type="text"
              name="slug"
              id="slug"
              placeholder="Leave blank to generate from the title"
              autoComplete="off"
              value={`${values.slug || ""}`}
              onChange={handleChange}
              onBlur={handleBlur}
              className="form-input"
            />
          </div>
        </div>

        <BlogSeoFields
          values={values}
          errors={errors}
          touched={touched}
          handleChange={handleChange}
          handleBlur={handleBlur}
        />

        <div className="row with-forms">
          <div className="col-md-6">
            <h5>Status</h5>
            <select
              name="status"
              id="status"
              value={values.status || "draft"}
              onChange={handleChange}
              onBlur={handleBlur}
              className="form-input"
            >
              <option value="draft">Draft</option>
              <option value="published">Published</option>
              <option value="archived">Archived</option>
            </select>
          </div>
          <div className="col-md-6">
            <h5>
              Published At (UTC)
              {values.status === "published" && <sup className="required">*</sup>}
            </h5>
            <input
              type="datetime-local"
              name="published_at"
              id="published_at"
              value={values.published_at || ""}
              onChange={handleChange}
              onBlur={handleBlur}
              className={errors.published_at && touched.published_at ? "form-input error" : "form-input"}
            />
            {errors.published_at && touched.published_at && <div className="error">{errors.published_at}</div>}
          </div>
        </div>

        <div className="row with-forms">
          <div className="col-md-12">
            <div className="blog-composer-heading">
              <div>
                <h5>
                  Content
                  <sup className="required">*</sup>
                </h5>
                <p>Write and review the article using the same content width and typography as the public blog.</p>
              </div>
              <div className="blog-composer-modes" role="group" aria-label="Composer view">
                {["edit", "split", "preview"].map((mode) => (
                  <button
                    key={mode}
                    type="button"
                    className={composerMode === mode ? "active" : ""}
                    onClick={() => setComposerMode(mode)}
                    aria-pressed={composerMode === mode}
                  >
                    {mode === "edit" ? "Editor" : mode === "split" ? "Split view" : "Preview"}
                  </button>
                ))}
              </div>
            </div>
            <textarea
              required={true}
              className="invisibled"
              name="content"
              value={`${stripHtml(values.content) || ""}`}
              onChange={(e) => e}
            ></textarea>
            <div className={`blog-composer blog-composer--${composerMode}`}>
              {composerMode !== "preview" && (
                <div className="blog-composer__editor">
                  <div className="blog-composer__panel-label">Editor</div>
                  <ImageEditor
                    className={stripHtml(values.content) ? '' : 'error'}
                    value={`${values.content || ""}`}
                    placeholder="Start writing your article…"
                    onChange={(value) => setFieldValue("content", value)}
                  />
                </div>
              )}
              {composerMode !== "edit" && (
                <div className="blog-composer__preview">
                  <div className="blog-composer__panel-label">Live preview</div>
                  <article className="blog-preview utf_blog_post utf_single_post">
                    {values.image_url ? (
                      <img className="blog-preview__image" src={values.image_url} alt="" />
                    ) : (
                      <div className="blog-preview__image-placeholder">Featured image preview</div>
                    )}
                    <div className="utf_post_content">
                      <h1>{values.title || "Untitled blog post"}</h1>
                      <div className="blog-preview__meta">
                        <span>{previewDate}</span>
                        <span className={`blog-preview__status blog-preview__status--${values.status || "draft"}`}>
                          {values.status || "draft"}
                        </span>
                      </div>
                      {stripHtml(values.content) ? (
                        <div
                          className="blog-preview__body"
                          dangerouslySetInnerHTML={{ __html: sanitizePreviewHtml(values.content) }}
                        />
                      ) : (
                        <p className="blog-preview__empty">Your article content will appear here as you type.</p>
                      )}
                    </div>
                  </article>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="row with-forms">
          <div className="col-md-12">
            <h5>
              Featured Image
              <sup className="required">*</sup>
            </h5>
          </div>
          <div className="col-md-2">
            <button
              type="button"
              className="btn btn-green"
              onClick={(e) => setOpeningModal("image")}
            >
              {values.image_url ? 'Change' : 'Add'} media
            </button>
          </div>
          <div className="col-md-10">
            {values.image_url && (
              <div>
                <img src={values.image_url} style={{ maxWidth: '250px' }} />
              </div>
            )}
          </div>
        </div>
        <div className="row with-forms">
          <div className="col-md-12">
            <h5>
              Tags
              <sup className="required">*</sup>
            </h5>
            <ReactTags
              tags={values.tag_list || []}
              suggestions={suggestions}
              onDelete={onDeleteTag}
              onAddition={onAddTag}
              allowNew={true}
              classNames={{
                searchInput: 'form-input',
              }}

            />
          </div>
        </div>

        <BlogFaqFields values={values} setFieldValue={setFieldValue} />

        <div className="row with-forms blog-bottom-content-editor">
          <div className="col-md-12">
            <h5>Bottom content</h5>
            <p className="blog-bottom-content-editor__help">
              Optional rich content shown below the FAQs to give readers a stronger closing section.
            </p>
            <ImageEditor
              value={`${values.bottom_content || ""}`}
              placeholder="Add a helpful conclusion, next steps, or related recommendations…"
              onChange={(value) => setFieldValue("bottom_content", value)}
            />
          </div>
        </div>

        <div className={`form-action`}>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={isLoading}
          >
            Save &amp; Edit
          </button>
        </div>
      </form>

      <GalleryModal
        maxNumberOfFiles={1}
        isOpen={!!openingModal}
        onCancel={(e) => setOpeningModal(null)}
        onSelectPhoto={handleSelect}
      />
    </React.Fragment>
  );
};

export default BlogEdit;
